import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { authService } from "../../../services/authService";
import "./r_sign_in 1.css";
import "./r_otp_verify.css";

const R_ForgotPasswordVerify: React.FC = () => {
    const [email, setEmail] = useState("");
    const [otpDigits, setOtpDigits] = useState<string[]>(Array(6).fill(""));
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);
    const [timer, setTimer] = useState(600);
    const [otpExpired, setOtpExpired] = useState(false);
    const [otpVerified, setOtpVerified] = useState(false);
    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [showNew, setShowNew] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);

    const otpRefs = useRef<(HTMLInputElement | null)[]>([]);
    const navigate = useNavigate();
    const location = useLocation();

    const initialEmail = useMemo(() => (location.state as { email?: string } | null)?.email || "", [location.state]);

    useEffect(() => {
        if (initialEmail) {
            setEmail(initialEmail);
            setTimer(600);
        }
        if (!initialEmail) {
            navigate("/forgot-password", { replace: true });
        }
    }, [initialEmail]);

    useEffect(() => {
        if (!email || timer <= 0) return;
        const id = setInterval(() => {
            setTimer((prev) => {
                if (prev <= 1) {
                    clearInterval(id);
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);
        return () => clearInterval(id);
    }, [email, timer]);

    useEffect(() => {
        if (!otpVerified && timer === 0) {
            setOtpExpired(true);
        }
    }, [otpVerified, timer]);

    const otpValue = otpDigits.join("");
    const isDisabled = loading || !email || otpValue.length !== 6;
    const canResend = !loading && !!email && timer === 0;
    // Only flag a mismatch once the user has actually typed into both fields -
    // showing it while confirmPassword is still empty would fire before
    // they've had a chance to type anything there.
    const passwordsMismatch = !!newPassword && !!confirmPassword && newPassword !== confirmPassword;
    const isResetDisabled = loading || !otpVerified || !newPassword || !confirmPassword || passwordsMismatch;

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (isDisabled) return;

        setError(null);
        setSuccessMessage(null);

        try {
            setLoading(true);
            const res = await authService.otpVerify({
                email,
                purpose: "password_reset",
                otp_code: otpValue,
            });
            setSuccessMessage(res?.message || "OTP verified successfully");
            setOtpVerified(true);
        } catch (err: any) {
            setError(err?.message || "Failed to verify OTP");
        } finally {
            setLoading(false);
        }
    };

    const handleResend = async () => {
        if (!canResend) return;
        setError(null);
        setSuccessMessage(null);
        try {
            setLoading(true);
            const res = await authService.otpRequest({ email, purpose: "password_reset" });
            setSuccessMessage(res?.message || "OTP sent successfully");
            setTimer(600);
            setOtpExpired(false);
            setOtpDigits(Array(6).fill(""));
        } catch (err: any) {
            setError(err?.message || "Failed to resend OTP");
        } finally {
            setLoading(false);
        }
    };

    const handleResetSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (isResetDisabled) return;
        setError(null);
        setSuccessMessage(null);
        try {
            setLoading(true);
            const res = await authService.passwordReset({
                email,
                otp_code: otpValue,
                new_password: newPassword,
                confirm_password: confirmPassword,
            });
            setSuccessMessage(res?.message || "Password reset successful");
            navigate("/login", { replace: true });
        } catch (err: any) {
            setError(err?.message || "Failed to reset password");
        } finally {
            setLoading(false);
        }
    };

    const handleOtpChange = (value: string, index: number) => {
        const digit = value.replace(/\D+/g, "").slice(0, 1);
        setOtpDigits((prev) => {
            const next = [...prev];
            next[index] = digit;
            return next;
        });
        if (digit && otpRefs.current[index + 1]) {
            otpRefs.current[index + 1]?.focus();
        }
    };

    const handleOtpKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, index: number) => {
        if (e.key === "Backspace" && !otpDigits[index] && index > 0) {
            otpRefs.current[index - 1]?.focus();
        }
        if (e.key === "ArrowLeft" && index > 0) {
            e.preventDefault();
            otpRefs.current[index - 1]?.focus();
        }
        if (e.key === "ArrowRight" && index < otpDigits.length - 1) {
            e.preventDefault();
            otpRefs.current[index + 1]?.focus();
        }
    };

    const handleOtpPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
        e.preventDefault();
        const pasted = e.clipboardData.getData("text").replace(/\D+/g, "").slice(0, 6);
        if (!pasted) return;
        const next = Array(6).fill("");
        pasted.split("").forEach((char, idx) => {
            next[idx] = char;
        });
        setOtpDigits(next);
        const lastIndex = Math.min(pasted.length, 6) - 1;
        if (lastIndex >= 0 && otpRefs.current[lastIndex]) {
            otpRefs.current[lastIndex]?.focus();
        }
    };

    return (
        <div className="container">
            <div className="keep-box-in-center">
                <img src="/images/right-emails-logo.svg" className="form-logo" alt="Right Emails" />

                <div className="form-box">
                    <p className="form-header">{otpVerified ? "Set Your Password" : "Verify OTP"}</p>
                    <p className="form-inform">
                        {otpVerified ? "Enter a new password for your account" : "Enter the code sent to your email"}
                    </p>

                    {error && <p className="error-text">{error}</p>}
                    {successMessage && <p className="success">{successMessage}</p>}

                    {!otpVerified ? (
                        <>
                            <div className="tch-section">
                                <span className="disabled-text">Code sent to {email}</span>
                            </div>

                            <form onSubmit={handleSubmit}>
                                <div className="tch-section">
                                    <label className="r-form-label">Enter Your OTP</label>
                                    <div className="otp-inputs-row" onPaste={handleOtpPaste}>
                                        {otpDigits.map((digit, idx) => (
                                            <input
                                                key={idx}
                                                ref={(el) => {
                                                    otpRefs.current[idx] = el;
                                                }}
                                                type="text"
                                                inputMode="numeric"
                                                pattern="\d*"
                                                maxLength={1}
                                                className={`r-form-email otp-digit-input${otpExpired ? " otp-digit-expired" : ""}`}
                                                value={digit}
                                                onChange={(e) => handleOtpChange(e.target.value, idx)}
                                                onKeyDown={(e) => handleOtpKeyDown(e, idx)}
                                                required
                                            />
                                        ))}
                                    </div>
                                </div>

                                <button
                                    type="submit"
                                    className={`form-submit ${loading ? "disabled" : ""}`}
                                    disabled={isDisabled}
                                >
                                    {loading ? "Verifying..." : "Verify OTP"}
                                </button>
                            </form>

                            <p className="asking">
                                {timer > 0 ? (
                                    <span className="disabled-text">Resend available in {Math.floor(timer / 60)}:{String(timer % 60).padStart(2, "0")}</span>
                                ) : (
                                    <span className="ask-sign-up" style={{ cursor: canResend ? "pointer" : "not-allowed" }} onClick={handleResend}>
                                        Resend OTP
                                    </span>
                                )}
                            </p>
                        </>
                    ) : (
                        <form onSubmit={handleResetSubmit}>
                            <div className="tch-section password-field">
                                <label className="r-form-label">New Password</label>
                                <div className="password-wrapper">
                                    <input
                                        type={showNew ? "text" : "password"}
                                        name="new_password"
                                        className="r-form-email"
                                        value={newPassword}
                                        onChange={(e) => setNewPassword(e.target.value)}
                                        required
                                        disabled={loading}
                                    />
                                    <img
                                        src={showNew ? "/images/grey-eye.png" : "/images/md-eye-off.png"}
                                        className="eye-icon"
                                        onClick={() => setShowNew(!showNew)}
                                    />
                                </div>
                            </div>

                            <div className="tch-section password-field">
                                <label className="r-form-label">Confirm Password</label>
                                <div className="password-wrapper">
                                    <input
                                        type={showConfirm ? "text" : "password"}
                                        name="confirm_password"
                                        className="r-form-email"
                                        value={confirmPassword}
                                        onChange={(e) => setConfirmPassword(e.target.value)}
                                        required
                                        disabled={loading}
                                    />
                                    <img
                                        src={showConfirm ? "/images/grey-eye.png" : "/images/md-eye-off.png"}
                                        className="eye-icon"
                                        onClick={() => setShowConfirm(!showConfirm)}
                                    />
                                </div>
                                {passwordsMismatch && <p className="error-text">Passwords do not match</p>}
                            </div>

                            <button
                                type="submit"
                                className={`form-submit ${loading ? "disabled" : ""}`}
                                disabled={isResetDisabled}
                            >
                                {loading ? "Saving..." : "Confirm"}
                            </button>
                        </form>
                    )}
                </div>
            </div>
        </div>
    );
};

export default R_ForgotPasswordVerify;
